<template>
  <AlertsDialog title="Snooze" :subtitle="subtitle" @close="$emit('close')">
    <fieldset class="alerts-accept__group">
      <legend class="alerts-accept__label">For how long</legend>
      <div class="alerts-accept__radios">
        <label v-for="(preset, i) in PRESETS" :key="preset.seconds">
          <input
            v-model="seconds"
            type="radio"
            :value="preset.seconds"
            :data-autofocus="i === 2 ? '' : undefined"
          >
          {{ preset.label }}
        </label>
      </div>
      <p class="alerts-accept__hint">Ends {{ endsLabel }}. Covers this alert's subject, so a re-open inside the window stays quiet too.</p>
    </fieldset>

    <label class="alerts-accept__block">
      <span class="alerts-accept__label">Reason <span class="alerts-accept__muted">· optional</span></span>
      <textarea v-model="reason" class="alerts-accept__input alerts-accept__textarea" placeholder="e.g. awaiting the customer's fix" />
    </label>

    <p class="alerts-accept__consequence">
      Nothing is posted about this alert until then. If it is still open when the snooze ends you get one "snooze ended, still open" reply, then its normal schedule.
    </p>
    <p v-if="error" class="alerts-accept__error" role="alert">{{ error }}</p>

    <template #actions>
      <button type="button" class="alerts-admin-board__mini-btn" @click="$emit('close')">Cancel</button>
      <button type="button" class="alerts-admin-board__mini-btn alerts-admin-board__mini-btn--primary" :disabled="saving" @click="submit">
        {{ saving ? 'Snoozing…' : `Snooze ${currentLabel}` }}
      </button>
    </template>
  </AlertsDialog>
</template>

<script setup>
import { computed, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import AlertsDialog from '@root/components/Alerts/AlertsDialog.vue';
import { formatMoment } from '@root/services/alertFormat';

// Plan 012 §8.1 — snooze presets 4 h / 1 d / 7 d / 30 d (max 90 d, enforced
// by the API). 7 days is preselected: the canonical snooze (the chronic
// php8.2/hrindependents FPM saturation).
const PRESETS = [
  { seconds: 4 * 3600, label: '4 hours' },
  { seconds: 86400, label: '1 day' },
  { seconds: 7 * 86400, label: '7 days' },
  { seconds: 30 * 86400, label: '30 days' },
];

const props = defineProps({
  alert: { type: Object, required: true },
  apiBase: { type: String, required: true },
});
const emit = defineEmits(['close', 'done']);

const api = useApi();
const seconds = ref(7 * 86400);
const reason = ref('');
const saving = ref(false);
const error = ref('');

const subtitle = computed(() => [props.alert.subject, props.alert.short_title].filter(Boolean).join(' · '));
const currentLabel = computed(() => PRESETS.find((p) => p.seconds === seconds.value)?.label || '');
const endsLabel = computed(() => formatMoment(Math.floor(Date.now() / 1000) + Number(seconds.value)));

async function submit() {
  saving.value = true;
  error.value = '';
  try {
    const data = await api.post(`${props.apiBase}/suppressions`, {
      kind: 'snooze',
      alert_id: props.alert.id,
      duration_seconds: Number(seconds.value),
      reason: reason.value.trim() || null,
    });
    emit('done', data);
  } catch (err) {
    error.value = err?.response?.data?.error || 'Couldn\'t snooze. Try again.';
  } finally {
    saving.value = false;
  }
}
</script>
